import React, {
  useEffect,
  ReactNode,
  useContext,
  useState,
  useMemo,
} from "react";
import {
  Box,
  Typography,
  Stack,
  useTheme,
  Button,
  Divider,
  TableRow,
  TableCell,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import PanelTable from "components/tables/PanelTable";
import { smallTableRowSx } from "utils";
import { Column, WIZARD_ACTION } from "types";
import WarningIcon from "@mui/icons-material/Warning";
import AuturaTextField from "components/inputs/AuturaTextField";
import { ImportFieldInput } from "./ImportFieldInput";
import {
  getValueForCombinedField,
  ImportWizardContext,
} from "components/importer/ImportWizardContext";
dayjs.extend(customParseFormat);

const DataPreviewTable = (): ReactNode => {
  const { t } = useTranslation();
  const { state, dispatch, fieldMetadata } = useContext(ImportWizardContext);
  const [editRow, setEditRow] = useState<number>(-1);
  const [value, setValue] = useState<string>("");
  const [valueIsValid, setValueIsValid] = useState<boolean>(false);
  const theme = useTheme();

  const currentField = state.selectedSystemField;
  const csvHeader = state.columnMappings[currentField];
  const functions = fieldMetadata[currentField];

  const formatter = functions?.formatter || ((value) => value);
  const validator = functions?.validator || (() => true);

  const getRawValueForRow = (
    row: Record<string, string>,
    csvHeader: string,
  ): string => {
    if (!csvHeader || !row) return "";
    const isMappedVehicleField = csvHeader.toLowerCase().startsWith("_vehicle");
    const mappedVehicleHeader = state.columnMappings["_parsed.vehicle"];
    const isCombinedField = csvHeader.startsWith("_combined_");
    const combinedFieldKey = isCombinedField ? csvHeader : "";
    if (isMappedVehicleField) {
      return row[mappedVehicleHeader];
    } else if (isCombinedField) {
      return getValueForCombinedField(row, combinedFieldKey);
    }
    return row[csvHeader];
  };

  const previewData: { raw: string; formatted: string; rowNumber: number }[] =
    useMemo(() => {
      if (!csvHeader) return [];
      // when editing a row, only show that row in the preview
      if (editRow > -1) {
        return [
          {
            raw: getRawValueForRow(state.modifiedData[editRow], csvHeader),
            formatted: state.modifiedData[editRow][csvHeader],
            rowNumber: editRow + 1,
          },
        ];
      }

      // 100 rows in total, rows with errors are shown first
      const _previewRowIndecies = [...state.focusedRows.slice(0, 100)];
      while (
        _previewRowIndecies.length < Math.min(100, state.modifiedData.length)
      ) {
        const randomIndex = Math.floor(
          Math.random() * state.modifiedData.length,
        );
        if (!_previewRowIndecies.includes(randomIndex)) {
          _previewRowIndecies.push(randomIndex);
        }
      }

      const previewData: {
        raw: string;
        formatted: string;
        rowNumber: number;
      }[] = [];
      for (const index of _previewRowIndecies) {
        const rawRow = state.csvData[index];
        const rawValue = getRawValueForRow(rawRow, csvHeader);
        const modifiedRow = state.modifiedData[index];
        const modifiedValue = modifiedRow[csvHeader];
        const formattedValue = formatter
          ? formatter(modifiedValue)
          : modifiedValue;

        previewData.push({
          raw: rawValue,
          formatted: formattedValue || modifiedValue || "",
          rowNumber: index + 1,
        });
      }

      return previewData.sort((a, b) => a.rowNumber - b.rowNumber);
    }, [
      state.selectedSystemField,
      state.modifiedData,
      editRow,
      state.columnMappings,
      csvHeader,
    ]);

  const columns: Column[] = [
    {
      name: `${t("row")} #`,
      sortKey: "rowNumber",
    },
    {
      name: t("imported_data", { ns: "importWizard" }),
    },
    {
      name: t("your_data", { ns: "importWizard" }),
      align: "right",
    },
  ];

  const handleRowClick = (rowNumber: number) => {
    setEditRow(rowNumber - 1);
    setValue(state.modifiedData[rowNumber - 1][csvHeader]);
  };

  const handleCancel = () => {
    setEditRow(-1);
    setValue("");
  };

  useEffect(() => {
    // reset some state when the field changes
    setValueIsValid(false);
    setEditRow(-1);
    setValue("");
  }, [currentField]);

  useEffect(() => {
    // check if the value is valid on value change
    if (editRow > -1) {
      setValueIsValid(validator(value));
    }
  }, [value]);

  const handleUpdateValue = (
    rowIndex: number,
    csvHeader = "",
    value: string,
  ) => {
    const updatedData = [...state.modifiedData];
    updatedData[rowIndex][csvHeader] = value;
    dispatch({
      type: WIZARD_ACTION.SET_MODIFIED_DATA,
      payload: updatedData,
    });
  };

  const rowHasWarning = (rowNumber: number) => {
    return state.focusedRows.includes(rowNumber - 1);
  };

  if (!state.selectedSystemField) return null;
  return (
    <React.Fragment>
      <Divider textAlign={"center"} role={"presentation"}>
        <Typography variant={"overline"}>
          {t("data_preview", { ns: "importWizard" })}
        </Typography>
      </Divider>
      <PanelTable<{ raw: string; formatted: string; rowNumber: number }>
        name="Data Preview"
        data={previewData}
        display="data"
        hideNoOptionsText
        maxHeight={"25vh"}
        readonly
        size="small"
        columns={columns}
        renderRow={(row, index) => {
          return (
            <TableRow
              key={row.rowNumber}
              sx={smallTableRowSx(index, theme)}
              onClick={() => handleRowClick(row.rowNumber)}
            >
              <TableCell>
                {rowHasWarning(row.rowNumber) ? (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {row.rowNumber}
                    <WarningIcon color="warning" fontSize="small" />
                  </Box>
                ) : (
                  row.rowNumber
                )}
              </TableCell>
              <TableCell>{row.formatted}</TableCell>
              <TableCell sx={{ textAlign: "right" }}>{row.raw}</TableCell>
            </TableRow>
          );
        }}
      />
      {editRow > -1 && (
        <Stack direction="column" spacing={0}>
          <Stack direction="row" spacing={3} display="flex" pb={2}>
            <Box sx={{ flex: 1 }}>
              <ImportFieldInput
                field={currentField}
                value={value}
                setValue={setValue}
              />
              <Typography color="error" variant="caption">
                {valueIsValid
                  ? ""
                  : t("invalid_field", {
                      ns: "importWizard",
                      field: t(currentField, { ns: "entities" }),
                    })}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <AuturaTextField
                name="originalValue"
                label={t("original_value", { ns: "importWizard" })}
                value={state.modifiedData[editRow][csvHeader]}
                fullWidth
                readonly
              />
            </Box>
          </Stack>
          <Stack
            direction="row"
            gap={4}
            display="flex"
            justifyContent="space-between"
          >
            <Button onClick={handleCancel} variant="text" color="error">
              {t("cancel")}
            </Button>
            <Button
              onClick={() => handleUpdateValue(editRow, csvHeader, value)}
              variant="text"
              color="primary"
              disabled={!valueIsValid}
            >
              {t("buttons.update", {
                name: t(currentField, { ns: "entities" }),
              })}
            </Button>
          </Stack>
        </Stack>
      )}
    </React.Fragment>
  );
};

export default DataPreviewTable;
